import { Component, OnInit } from '@angular/core';
import { Deeplinks } from '@awesome-cordova-plugins/deeplinks/ngx';
import { SafariViewController } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { NavController, Platform } from '@ionic/angular';
import { EMPTY, of, Subject } from 'rxjs';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ErrorService } from 'src/app/services/error/error.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { ITenantLite } from 'src/app/services/tenant/models/tenant-lite.model';
import { catchError, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { InAppBrowser } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Logger, LoggingService } from 'ionic-logging-service';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
})
export class LoginPage implements OnInit {
  public  authWaiting: boolean = false;
  public queryParams: { [key: string]: string | undefined };
  public  showTenantNameSpinner = false;
  public  buildInAuthFormSubmitted: boolean = false;
  public  buildInAuthWaiting: boolean = false;
  public  buildInAuthUrl: string = null;
  public  errorMessage: string = null;
  public  errorStatus: number = undefined;
  public  isCordova: boolean = this.platformService.isCordova();
  public  isOnline = this.networkService.hasInternetAccess;
  private logger: Logger;
  public  loginFailed: string = null;
  public  loginMethod: string = null;
  public  resolvingHttpRequest: boolean = false;
  public  tenantList: Array<ITenantLite> = null;
  private unSubscribe = new Subject<void>();
  public  showTenantDoesNotExist = false;
  public  showTenantCouldNotBeLoaded = false;
  public  showTenantIsDeactivated = false;

  public  userInputTenantDomain: string = null;
  public  userInputEnvironmentDomain: string = null;
  public  signInCode: string | null = null;
  public  showTenantDomainInput = false;

  private appLink: string;
  private endpointLogInWepAppTenant: string;
  private endpointLogInResponse_WepApp: string;
  private endpointLogInResponse_Native: string;

  constructor (
    private authService: AuthService,
    private broadcaster: Broadcaster,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private deeplinks: Deeplinks,
    private inAppBrowser: InAppBrowser,
    private loggingService: LoggingService,
    private navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    public platform: Platform,
    private platformService: PlatformService,
    private route: ActivatedRoute,
    private safariViewController: SafariViewController,
    private tenantService: TenantService
  ) {
    this.logger = loggingService.getLogger("[LoginPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
    this.errorService.stateError = false;
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('');
    this.logger.info('ngOnInit()');
    this.platform.ready().then(() => {
      if (this.isCordova) {
        this.environmentService.resetEnvironmentEndpoints().subscribe(() => {
          this.initPage();
          this.unSubscribe.next();
          this.unSubscribe.complete();
        });
      }
    });
  }

  private initPage(): void {
    this.logger.info('initPage()');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      this.queryParams = params;
      this.errorMessage = params['ACCESS_DENIED'] ? 'AccessDenied' : null;
      this.listenForRedirect();
    });
  }

  public listenForRedirect() {
    const methodName = 'listenForRedirect() ';
    this.logger.info(methodName);
    this.deeplinks.route({'' : LoginPage}).pipe(
      take(1),
      tap(match => {
        this.logger.info(methodName + 'deeplinks.route: MATCH - code.length' + match.$link.url.split("code=")[0].length);
        if (match.$args && match.$args.idpLoginToken) {
          this.logger.info(methodName + 'deeplinks - match.$args.idpLoginToken ' + match.$args.idpLoginToken.length);
          const idpLoginToken = match.$args['idpLoginToken'];
          this.tenantService.setIdpLoginToken(idpLoginToken);
        }
        this.signInCode = match.$args && match.$args.code ?  match.$args.code : null;
      }),
      catchError(e => {
        this.logger.info(methodName + ' Error in returning deeplinks.route: NO_MATCH');
        this.logger.info('Error' + JSON.stringify(e));
        this.showTenantNameSpinner = false;
        return of(null);
      })
    ).subscribe(() => {
      this.handleUserAuthentication();
    })
  }

  private handleUserAuthentication(): void {
    if (this.signInCode) {
      this.authService.authenticateSignedInUser(this.signInCode, this.userInputTenantDomain).pipe(take(1))
      .subscribe({
        next: () => {
          this.logger.info('handleUserAuthentication() - verified');
          this.authSuccessful();
        },
        error: (error) => {
          this.logger.error('handleUserAuthentication() ERROR');
          this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
          .subscribe(result => {
            if (result === 'unauthenticated') {
              this.logger.info('User unauthenticated');
            }
          });
        }
      });
    }
  }

  public authSuccessful(): void {
    // continue the login process
    this.logger.info('authSuccessful()');
    this.broadcaster.broadcast('ENABLE_RESUME');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      const queryParams = params['registrationCode'] ? { registrationCode: params['registrationCode'] } : {};
      this.navController.navigateRoot(['/tenant-select'], { queryParams })
    });
  }

  public signIn(loginMethod) {
    this.loginMethod = loginMethod;
    this.logger.info('(click) signIn(loginMethod): ' + loginMethod);
    this.broadcaster.broadcast('DISABLE_RESUME');
    this.authWaiting = true;
    let platform: string = '';
    if (loginMethod === 'ms') {
      this.openBrowser(this.environmentService.getEnvironmentEndpoint['endpointLogInMS']);
    } else if (loginMethod === 'google') {
      this.openBrowser(this.environmentService.getEnvironmentEndpoint['endpointLogInGoogle']);
    }
  }

  private openBrowser(endpoint) {
    if (this.isCordova) {
      this.logger.info('signIn() using safariViewController with inAppBrowser as fallback and for web');
      this.appLink = this.environmentService.getEnvironmentEndpoint['appLink'];
      let url: string = endpoint + this.appLink;
      this.safariViewController.isAvailable()
      .then((available: boolean) => {
        if (available) {
          this.safariViewController.show({
            url: url
          })
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((response) => {
            this.logger.info('signIn() safariViewController response: ' + response.event);
            this.unSubscribe.next();
            this.unSubscribe.complete();
          });
        } else {
          this.logger.info('signIn() safariViewController not available using inAppBrowser to open device browser');
          // fallback to inAppBrowser,
          this.inAppBrowser.create(url, '_system');
        }
      });
    } else {
      this.logger.info('signIn() - WEB');
      let webUrl: string = endpoint + location.origin;
      this.authWaiting = false;
      this.loginMethod = null;
      window.open(webUrl, '_self');
    }
  }

  public showInputForTenantDomain(): void {
    this.logger.info('(click) showInputForTenantDomain()');
    this.showTenantDomainInput = true;
  }

  public checkUserTenant() {
    this.logger.info('(click) checkUserTenant() userInputTenantDomain: ' + JSON.stringify(this.userInputTenantDomain));
    this.showTenantNameSpinner = true;
    this.userInputTenantDomain = this.verifyTenantDomain();
    // check for tenantless sign-in
    this.userInputEnvironmentDomain = this.checkForTenantlessDomain(this.userInputTenantDomain);
    this.tenantService.setTenantInputValues(this.userInputTenantDomain, this.userInputEnvironmentDomain);
    this.logger.info('checkUserTenant() tenantDomain: ' + JSON.stringify(this.userInputTenantDomain));
    // set the environment variables from the input domain
    this.environmentService.setEnvironmentVariablesFromUrl(this.userInputTenantDomain).pipe(
      take(1),
      switchMap(() => {
        if (this.userInputEnvironmentDomain) {
          this.logger.info('Tenantless sign-in detected for: ' + this.userInputEnvironmentDomain);
          this.showTenantNameSpinner = false;
          this.goToTenantlessSignIn();
          return EMPTY;
        }
        // if not tenantless - continue with tenant existence check
        return this.tenantService.checkIfUserTenantDomainExists(this.userInputTenantDomain).pipe(
          tap(() => {
            this.logger.info('Tenant exists -> goToSignInApp()');
            this.goToSignInApp();
          }),
          catchError((error) => {
            this.logger.error('## ERROR TENANT_OR_DOMAIN DOES_NOT_EXIST: ' + JSON.stringify(error));
            this.environmentService.resetEnvironmentEndpoints().subscribe(() => {
              this.showTenantNameSpinner = false;
              if (error.status === 404) {
                this.logger.info('checkIfUserTenantDomainExists() httpErrorResponse: 404 - Printix home does not exist');
                this.showTenantDoesNotExist = true;
              } else if (error.status === 410) {
                this.logger.info('checkIfUserTenantDomainExists() httpErrorResponse: 410 - Printix home no longer exist ');
                this.showTenantIsDeactivated = true;
              } else if ((error.status === 503 || error.status === 0) && this.userInputTenantDomain.includes('.printix.dev')) {
                this.logger.info('checkIfUserTenantDomainExists() httpErrorResponse: 503 - Tenant could not be loaded');
                this.errorStatus = error.status;
                this.showTenantCouldNotBeLoaded = true;
              } else {
                this.errorService.handleHttpClientResponseError(error, 'GET', 'checkIfUserTenantDomainExists()')
                .subscribe(result => {
                  if (result === 'unauthenticated') {
                    this.logger.error('User unauthenticated');
                  }
                });
              }

            });
            return EMPTY;
          })
        );
      })
    ).subscribe();
  }

  private verifyTenantDomain(): string { // for test environments
    const inputTenantName = this.userInputTenantDomain ? this.userInputTenantDomain.toLowerCase() : null;
    if (inputTenantName.includes('.printix.dev')) {
      return inputTenantName;
    } else {
      return inputTenantName ? inputTenantName + '.printix.net' : inputTenantName;
    }
  }

  private checkForTenantlessDomain(domain: string): string | null {
    this.logger.info('checkForTenantlessDomain() - domain: ' + domain);
    const allowedDomains = [
      'devenv.printix.net',
      'devenv.us.printix.net',
      'testenv.printix.net',
      'testenv.us.printix.net',
      'prod.printix.net',
      'us.printix.net'
    ];

    // Check for exact matches in the allowedDomains list
    if (allowedDomains.includes(domain)) {
      return domain;
    }

    // check for the pattern devXX.printix.dev (no tenant name before devXX)
    const parts = domain.split('.');
    // Must be exactly 3 parts
    if (parts.length !== 3) return null;
    const [dev, printix, devDomain] = parts;
    const num = parseInt(dev.slice(3), 10);
    if (
      dev.startsWith('dev') &&
      !isNaN(num) &&
      printix === 'printix' &&
      devDomain === 'dev'
    ) {
      // Return the environment domain in the format devXX.printix.dev
      return domain;
    }

    return null;
  }

  private goToTenantlessSignIn(): void {
    this.logger.info('handleTenantlessSignIn()');
    this.broadcaster.broadcast('DISABLE_RESUME');
    this.appLink = this.environmentService.getEnvironmentEndpoint['appLink']; // 'printixapp://'
    const signInEndpoint = this.environmentService.getEnvironmentEndpoint['endpointLogInTenantLess'] // 'https://auth.dev04env.printix.net/oauth/authorize?response_type=code&client_id=web_app&redirect_uri=';
    let signInAppUrl = signInEndpoint + this.appLink;
    console.log('signInAppUrl', signInAppUrl);
    this.safariViewController.isAvailable()
      .then((available: boolean) => {
        if (available) {
          this.safariViewController.show({
            url: signInAppUrl
          })
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((response) => {
            this.logger.info('safariViewController - SignInApp response: ' + response.event);
            this.authWaiting = false;
          }, (error) => {
            this.authWaiting = false;
          });
        } else {
          this.logger.info('safariViewController - not available, using inAppBrowser to open device browser');
          // fallback to inAppBrowser
          this.inAppBrowser.create(signInAppUrl, '_system');
        }
      });
  }

  public goToSignInApp() {
    console.log('## GO_TO_SIGN_IN_APP ##');
    const methodName = 'goToSignInApp() ';
    this.logger.info(methodName);
    this.broadcaster.broadcast('DISABLE_RESUME');
    let signInAppUrl: string;
    if (this.isCordova) {
      this.endpointLogInWepAppTenant = this.environmentService.getEnvironmentEndpoint['endpointLogInWepAppTenant'] + this.userInputTenantDomain; // 'https://auth.printix.net/oauth/authorize/tenant/' + 'abc.printix.net
      this.appLink = this.environmentService.getEnvironmentEndpoint['appLink']; // 'printixapp://'
      const returnDomain = this.appLink + '?' + this.userInputTenantDomain; // 'printixapp://?abc.printix.net'
      this.endpointLogInResponse_Native = this.environmentService.getEnvironmentEndpoint['endpointLogInResponse_Native'] + returnDomain; // '?response_type=code&client_id=web_app&redirect_uri='
      signInAppUrl = this.endpointLogInWepAppTenant + this.endpointLogInResponse_Native;
      // signInAppUrl example:
      // 'https://auth.printix.net/oauth/authorize/tenant/' + 'abc.printix.net' + '?response_type=code&client_id=web_app&redirect_uri=' + 'printixapp://?abc.printix.net'
      this.logger.info(methodName + 'NATIVE signInAppUrl: ' + JSON.stringify(signInAppUrl));
      this.safariViewController.isAvailable()
      .then((available: boolean) => {
        if (available) {
          this.safariViewController.show({
            url: signInAppUrl
          })
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((response) => {
            this.logger.info(methodName, 'safariViewController - SignInApp response: ' + response.event);
            this.showTenantNameSpinner = false;
            this.authWaiting = false;
          }, (error) => {
            this.showTenantNameSpinner = false;
            this.authWaiting = false;
          });
        } else {
          this.logger.info(methodName, 'safariViewController - not available, using inAppBrowser to open device browser');
          // fallback to inAppBrowser
          this.inAppBrowser.create(signInAppUrl, '_system');
        }
      });
    } else {
      this.endpointLogInWepAppTenant = this.environmentService.getEnvironmentEndpoint['endpointLogInWepAppTenant'] + this.userInputTenantDomain; // 'https://auth.printix.net/oauth/authorize/tenant/'
      this.endpointLogInResponse_WepApp = this.environmentService.getEnvironmentEndpoint['endpointLogInResponse_WepApp']; // '?response_type=code&client_id=web_app&redirect_uri=https://',
      const returnDomain = this.userInputTenantDomain; // 'abc.printix.net'
      signInAppUrl = this.endpointLogInWepAppTenant + this.endpointLogInResponse_WepApp + returnDomain;
      // signInAppUrl example:
      // 'https://auth.printix.net/oauth/authorize/tenant/' + 'abc.printix.net' + '?response_type=code&client_id=web_app&redirect_uri=https://' + 'abc.printix.net'
      this.logger.info(methodName + 'WEB signInAppUrl: ' + JSON.stringify(signInAppUrl));
      this.authWaiting = false;
      this.loginMethod = null;
      window.open(signInAppUrl, '_self');
    }
  }

  public hideErrorText(event) {
    this.showTenantDoesNotExist = false;
    this.showTenantIsDeactivated = false;
  }
}
